/**
 * Native helper — thin wrapper around Capacitor plugins with graceful
 * degradation on the web build.
 *
 * Every function is safe to call from anywhere:
 *   • On iOS / Android → hits the native API
 *   • On the browser build → falls back to the browser equivalent (or no-op)
 *
 * Import individual helpers, not the module:
 *   import { hapticLight, shareUrl, isNative } from '@/lib/native'
 */

import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Share } from '@capacitor/share';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { PushNotifications } from '@capacitor/push-notifications';
import { App as CapApp } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { Preferences } from '@capacitor/preferences';
import { StatusBar, Style as StatusBarStyle } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

// ============ HAPTICS ============
export async function hapticLight() {
  if (!isNative) return;
  try { await Haptics.impact({ style: ImpactStyle.Light }); } catch {}
}
export async function hapticMedium() {
  if (!isNative) return;
  try { await Haptics.impact({ style: ImpactStyle.Medium }); } catch {}
}
export async function hapticSuccess() {
  if (!isNative) return;
  try { await Haptics.notification({ type: NotificationType.Success }); } catch {}
}
export async function hapticError() {
  if (!isNative) return;
  try { await Haptics.notification({ type: NotificationType.Error }); } catch {}
}

// ============ SHARE ============
/**
 * Open the OS native share sheet with a link + text.
 * Falls back to `navigator.share` (mobile browsers) then clipboard.
 */
export async function shareUrl({ title, text, url }) {
  if (isNative) {
    try {
      await Share.share({ title, text, url, dialogTitle: title });
      return { method: 'native' };
    } catch (err) {
      // User cancelled — treat as no-op.
      return { method: 'cancelled' };
    }
  }
  // Web fallback
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return { method: 'web-share' };
    } catch { /* user cancelled */ }
  }
  // Clipboard last resort
  try {
    await navigator.clipboard.writeText(url);
    return { method: 'clipboard' };
  } catch {
    return { method: 'unsupported' };
  }
}

// ============ CAMERA ============
/**
 * Open the native camera OR photo library picker.
 * Returns { dataUrl, format } (data URL is easiest to post as JSON to backend).
 *
 * `source: "camera" | "photos" | "prompt"` (default = prompt to pick)
 * `quality: 0..100` (default 80)
 */
export async function takePhoto({ source = 'prompt', quality = 80 } = {}) {
  if (!isNative) {
    // Fall through to the browser's <input type="file" accept="image/*" capture>
    return { dataUrl: null, format: null, cancelled: true, unsupported: true };
  }
  const sourceMap = {
    camera: CameraSource.Camera,
    photos: CameraSource.Photos,
    prompt: CameraSource.Prompt,
  };
  try {
    const photo = await Camera.getPhoto({
      quality,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: sourceMap[source] ?? CameraSource.Prompt,
      // saveToGallery: false,   // keep it private
    });
    return { dataUrl: photo.dataUrl, format: photo.format, cancelled: false };
  } catch (err) {
    return { dataUrl: null, format: null, cancelled: true, error: err?.message };
  }
}

// ============ PUSH NOTIFICATIONS ============
/**
 * Prompt for push permission and register with APNs/FCM. Resolves with the
 * device token so the caller can POST it to the backend.
 *
 * Listeners are auto-detached when the caller calls the returned `dispose()`.
 */
export async function registerForPush({ onToken, onReceive, onAction }) {
  if (!isNative) {
    return { supported: false, dispose: () => {} };
  }

  const listeners = [];
  const dispose = async () => {
    for (const l of listeners) { try { await l.remove(); } catch {} }
  };

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') {
    return { supported: true, granted: false, dispose };
  }

  if (onToken) {
    listeners.push(await PushNotifications.addListener('registration', (token) => onToken(token.value)));
  }
  if (onReceive) {
    listeners.push(await PushNotifications.addListener('pushNotificationReceived', onReceive));
  }
  if (onAction) {
    listeners.push(await PushNotifications.addListener('pushNotificationActionPerformed', onAction));
  }
  listeners.push(await PushNotifications.addListener('registrationError', (e) => {
    // eslint-disable-next-line no-console
    console.warn('push registration error', e);
  }));

  await PushNotifications.register();
  return { supported: true, granted: true, dispose };
}

// ============ APP / DEEP LINKS ============
/**
 * Wire up handling of `symposia://…` deep links AND universal links.
 * `onUrl(pathname)` is called with the path portion (e.g. `/conferences/xyz`).
 */
export function listenForDeepLinks(onUrl) {
  if (!isNative) return () => {};
  const p = CapApp.addListener('appUrlOpen', (event) => {
    try {
      const u = new URL(event.url);
      // symposia://conferences/xyz  → pathname='//conferences/xyz'
      const path = (u.pathname || '/').replace(/^\/+/, '/');
      onUrl?.(path + (u.search || ''));
    } catch {
      /* malformed URL */
    }
  });
  // Non-await pattern — return a sync unsubscribe.
  return () => { p.then((l) => l.remove()).catch(() => {}); };
}

// ============ NETWORK / OFFLINE ============
export async function getNetworkStatus() {
  if (!isNative) {
    return { connected: typeof navigator !== 'undefined' ? navigator.onLine : true, connectionType: 'unknown' };
  }
  try {
    const s = await Network.getStatus();
    return { connected: s.connected, connectionType: s.connectionType };
  } catch {
    return { connected: true, connectionType: 'unknown' };
  }
}

export function onNetworkChange(cb) {
  if (!isNative) {
    const on = () => cb({ connected: true, connectionType: 'unknown' });
    const off = () => cb({ connected: false, connectionType: 'none' });
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }
  const p = Network.addListener('networkStatusChange', cb);
  return () => { p.then((l) => l.remove()).catch(() => {}); };
}

// ============ PREFERENCES (persistent K/V, better than localStorage on native) ============
export const prefs = {
  async get(key) {
    if (!isNative) {
      const v = window.localStorage.getItem(key);
      return v == null ? null : v;
    }
    const { value } = await Preferences.get({ key });
    return value ?? null;
  },
  async set(key, value) {
    if (!isNative) return window.localStorage.setItem(key, value);
    return Preferences.set({ key, value });
  },
  async remove(key) {
    if (!isNative) return window.localStorage.removeItem(key);
    return Preferences.remove({ key });
  },
};

// ============ SPLASH / STATUS BAR ============
export async function hideSplash() {
  if (!isNative) return;
  try { await SplashScreen.hide(); } catch {}
}
export async function setStatusBarLight() {
  if (!isNative) return;
  try { await StatusBar.setStyle({ style: StatusBarStyle.Light }); } catch {}
}
export async function setStatusBarDark() {
  if (!isNative) return;
  try { await StatusBar.setStyle({ style: StatusBarStyle.Dark }); } catch {}
}
